import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { EM_ACTION, FIELD_KIND, FeiKeyDef, MathUtils, WidgetLine } from '@wizeoo/akm-compute-lib';
import { WizBooleanUtils, WizDateUtils, WizMathUtils, WizNumberUtils, WIZ_BOOLEAN,
  WIZ_PHYSIC_POSITION, 
  IWizTranslationEntry} from '@wizeoo/akm-core-lib';
import {
  WizAgGrid,
  WizBasePopUp,
  WizCheckbox,
  WizDatasource,
  WizDateField,
  WizIcon,
  WizLabel,
  WizNumericInput,
  WizPopUp,
  WizTextInput,
  WizValidationContainerDirective,
  WizValidationError,
  WizTranslationManager,
  WizTimefield,
  WizTranslationData,
  WizTextarea,
  WizTranslationBasic
} from '@wizeoo/akm-component-lib';
import { PopUtils } from '../../../../../framework/components/pop/pop-utils';
import { UpdateRequest } from '../../../../../command/core/sync/sync.service';
import { IWidget, PivotCellEditPopData } from '../../../../../manager/widgets/i-widget';
import { FeiVO } from '../../../../../vo/workingdata/fei.vo';
import { clone } from 'lodash-es';
import { Measure, TranslatedEmb } from '@wizeoo/akm-message-lib';
import { ColorPickerPop } from '../../../../colorpicker/color-picker.pop';
import { FreedataValueVO } from '../../../../../vo/core/freedatavalue.vo';
import { FreedataArgsVO } from '../../../../../vo/core/freedataargs.vo';
import { WIDGET_GLOBAL_KIND } from '../../../../../model/enums';
import { TranslatableLabelVO } from '../../../../../vo/common/translation/translatable-label.vo';

@Component({
  // popUp d'édition des cellules de grid
  selector: 'pivot-cell-edit-pop',
  templateUrl: './pivot-cell-edit.pop.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PivotCellEditPop extends WizBasePopUp implements AfterViewInit {
  /** Zone de validation des champs de saisie */
  @ViewChild('mainGP', { read: WizValidationContainerDirective, static: false })
  private mainGP: WizValidationContainerDirective;

  @ViewChild('lbTitle', { read: WizLabel, static: false }) lbTitle: WizLabel; // titre de la popUp (fullcode)
  @ViewChild('cb', { read: WizCheckbox }) public cb: WizCheckbox;
  @ViewChild('ti', { read: WizTextInput }) public ti: WizTextInput;
  @ViewChild('ni', { read: WizNumericInput }) public ni: WizNumericInput;
  @ViewChild('da', { read: WizDateField }) public da: WizDateField;
  @ViewChild('tf', { read: WizTimefield }) public tf: WizTimefield;
  @ViewChild('ta', { read: WizTextarea }) public ta: WizTextarea;
  @ViewChild('trsLabel', { read: WizTranslationBasic }) public trsLabel: WizTranslationBasic;


  @ViewChild('agGrid') agGrid: WizAgGrid<WidgetLine>;
  @ViewChild('lblShowHide', { read: WizLabel }) lblShowHide: WizLabel; // label pour montrer/masquer le détail

  @ViewChild('cellBlockBack') cellBlockBack: ElementRef; // zone de dessin de la couleur de background


  public static baseWidth: number = 600;
  public static baseHeight: number = 150;
  public static fullHeight: number = 350;

  public label: string; // libellé du champ pour les zones de saisie

  /** la DS du grid */
  public mainDS: WizDatasource<WidgetLine> = new WizDatasource<WidgetLine>().build();

  /** l'instance de FEI manipulée par ce composant */
  public fei: FeiVO = null;

  public widget: IWidget; // accesseur sur le pivot

  public nbDecimal: number = WizNumberUtils.nbDecimal; // nb décimal pour le numeric input

  public isMultiple: boolean = false; // il existe plus d'une ligne.

  public isKey: boolean = false;
  public isBoolean: boolean = false;
  public isNum: boolean = false;
  public isText: boolean = false;
  public isDate: boolean = false;
  public isTime: boolean = false;
  public isComment: boolean = false;
  public isTranslatable: boolean = false;
  public usecolor: boolean = false;


  public booleanValue: WIZ_BOOLEAN = WIZ_BOOLEAN.UNKNOWN;
  public textValue: string = null;
  public numValue: number = null;
  public dateValue: number = null;
  public translationData: WizTranslationData = null;

  private get popupData(): PivotCellEditPopData {
    return this.datas as PivotCellEditPopData;
  }

  private get fkd(): FeiKeyDef {
    return this.popupData.fkd;
  }

  private get linePack(): WidgetLine[] {
    return this.popupData.pack;
  }

  private get fdv(): FreedataValueVO {
    return this.popupData.fdv;
  }

  private get arg(): FreedataArgsVO {
    return this.popupData.arg;
  }

  /**
   * mode BASELINE ou FREEDATA
   */
  private get mode(): string {
    return this.popupData.kind;
  }


  constructor(public elementRef: ElementRef, protected cdref: ChangeDetectorRef) {
    super();
  }

  show(): void {
    this.widget = clone(this.popupData.widget); // clone, on touche pas au widget source
    this.fei = this.widget.fei as FeiVO;
    this.isMultiple = this.popupData.kind === WIDGET_GLOBAL_KIND.BASELINE && this.linePack?.length > 1;
    this.feedDatasources();
  }

  /** sur séquence de démarrage, alimentation des listes avec leurs DS */
  public feedDatasources(): void {
    this.lbTitle.wizLabel = this.datas.title;

    setTimeout(() => {
      // on attend que tout soit prêt...
      
      // d'abord les colonnes, elles forment la clé d'aggrégat
      if(this.mode === WIDGET_GLOBAL_KIND.BASELINE) {
        
        if(this.isMultiple) {
          this.agGrid.api.setColumnDefs([]); // on rince les colonnes
          let cols: any[] = this.widget.model.feiEngine.buildColumnsForMassEdit(this.fei, this.widget, this.fkd.fullCode);
          this.agGrid.api.setColumnDefs(cols); // générer les colonnes depuis le contexte

          this.mainDS.setSource(this.linePack); // alimentation de la DS des lignes // avec un max
        }
        this.label = this.datas.label; // libellé de la zone de texte (reprise du libellé de colonne)

        const meas: Measure = this.fkd.measure;
        if (meas.dataKind) {
          switch (meas.dataKind) {
            case FIELD_KIND.BOOLEAN:
              this.isBoolean = true;
              this.booleanValue = WizBooleanUtils.stringToBoolean(this.widget.model.aggregateEngine.BOOLEAN(this.fei, this.linePack, this.fkd));
              break;
            case FIELD_KIND.COLOR:
              this.usecolor = true;
              this.textValue = this.datas.value;
              break;
            case FIELD_KIND.INT:
            case FIELD_KIND.NUMBER:
              this.isNum = true;
              this.numValue = Number(this.datas.value);
              this.ni.wizMinNumber = !!meas.minValue ? Number(meas.minValue) : null;
              this.ni.wizMaxNumber = !!meas.maxValue ? Number(meas.maxValue) : null;
              this.ni.nbDecimal = !!meas.nbDecimal ? meas.nbDecimal : 0;
              this.ni.allowNegative = !!meas.allowNegative;
              break;
            case FIELD_KIND.DATE:
              this.isDate = true;
              this.dateValue = Number(this.datas.value);
              this.da.wizMinDate = !!meas.minValue ? WizDateUtils.stringToDate(meas.minValue) : null;
              this.da.wizMaxDate = !!meas.maxValue ? WizDateUtils.stringToDate(meas.maxValue) : null;
              break;
            case FIELD_KIND.TIME:
              this.isTime = true;
              this.textValue = this.datas.value;
              break;
            case FIELD_KIND.STRING:
            case FIELD_KIND.COMMENT:
              this.ti.wizMaxChars = !!meas.maxValue ? Number(meas.maxValue) : 50;
              this.isText = true;
              this.textValue = this.datas.value;
              break;
          }
        }
      } else { // mode freedata
        // console.log('fdmode', this.fdv, this.arg, this.datas.value);
        switch (this.arg.dataType) {
          case FIELD_KIND.BOOLEAN:
            this.isBoolean = true;
            this.booleanValue = WizBooleanUtils.stringToBoolean(this.datas.value);
            break;
          case FIELD_KIND.COLOR:
            this.usecolor = true;
            this.textValue = this.datas.value;
            break;
          case FIELD_KIND.INT:
          case FIELD_KIND.NUMBER:
            this.isNum = true;
            this.numValue = Number(this.datas.value);
            this.ni.wizMinNumber = !!this.arg.minValue ? Number(this.arg.minValue) : null;
            this.ni.wizMaxNumber = !!this.arg.maxValue ? Number(this.arg.maxValue) : null;
            this.ni.nbDecimal = this.arg.dataType === FIELD_KIND.INT ? 0: 2;
            this.ni.allowNegative = true;
            break;
          case FIELD_KIND.DATE:
            this.isDate = true;
            this.dateValue = Number(this.datas.value);
            this.da.wizMinDate = !!this.arg.minValue ? WizDateUtils.numberToDate(this.arg.minValue) : null;
            this.da.wizMaxDate = !!this.arg.maxValue ? WizDateUtils.numberToDate(this.arg.maxValue) : null;
            break;
          case FIELD_KIND.TIME:
            this.isTime = true;
            this.textValue = this.datas.value;
            break;
          case FIELD_KIND.STRING:
            this.ti.wizMaxChars = !!this.arg.maxValue ? Number(this.arg.maxValue) : 50;
            this.isText = true;
            this.textValue = this.datas.value;
            break;
          case FIELD_KIND.COMMENT:
            this.ta.wizMaxChars = !!this.arg.maxValue ? Number(this.arg.maxValue) : 50;
            this.isComment = true;
            this.textValue = this.datas.value;
            break;
          case FIELD_KIND.TRS_LABEL:
            // console.log('TRS_LABEL', this.fdv, this.arg);
            let lblVo: TranslatableLabelVO = this.widget.model.localeEngine.newTranslatableVO(this.fdv, this.arg.code);
            this.trsLabel.translationData = this.widget.model.localeEngine.buildTranslationData(lblVo);
            this.isTranslatable = true;
            break;
        }
      }



      if (this.isText) {
        this.ti.wizValue = this.textValue;
        setTimeout(() => {
          this.ti.wizFocus();
        }, 1);
      } else if (this.isNum) {
        this.ni.wizNumber = this.numValue;
        setTimeout(() => {
          this.ni.wizFocus();
        }, 1);
      } else if (this.isDate) {
        this.da.wizNumberValue = this.dateValue;
        setTimeout(() => {
          this.da.wizFocus();
        }, 1);
      } else if (this.isTime) {
        this.tf.wizHourValue = this.textValue;
        setTimeout(() => {
          this.tf.wizFocus();
        }, 1);
      } else if (this.isBoolean) {
        this.cb.wizCheckState = this.booleanValue;
        setTimeout(() => {
          this.cb.wizFocus();
        }, 1);
      } else if (this.isComment) {
        this.ta.wizValue = this.textValue;
        setTimeout(() => {
          this.ta.wizFocus();
        }, 1);
      }
      // TODO gestion traduction
    }, 0);
  }

  /** Sauvegarde, même valeur */
  public saveSingle(): void {
    const errors = this.mainGP.check(true, true);
    if (errors?.length > 0) {
      errors.forEach((wve: WizValidationError) => {
        let mess: string = WizTranslationManager.getInstance().translateKey(wve.wizError, wve.wizErrorParams);
        WizPopUp.toast(mess, WizIcon.TOAST_ERROR).show();
      });
      return; // pas de changement
    }

    let result: string = '';
    if (this.isBoolean) {
      // un bool
      result = WizMathUtils.booleanToString(this.cb.wizCheckState);
    } else if (this.isText) {
      // une mesure de type texte
      result = this.ti.wizValue;
    } else if (this.isNum) {
      // une mesure numérique
      if (!!this.ni.wizValue) {
        result = this.ni.wizNumber.toString();
      }
    } else if (this.isDate) {
      // une mesure de type date
      result = this.da.wizDateValue ? WizDateUtils.dateToString(this.da.wizDateValue) : null;
    } else if (this.isTime) {
      // une mesure de type heure
      result = this.tf.wizHourValue ? this.tf.wizHourValue : null;
    }
    else if (this.usecolor) {
      // une mesure de type date
      result = this.textValue;
    }
    else if (this.isComment) {
      // une mesure de type commentaire
      result = this.ta.wizValue;
    } else if (this.isTranslatable) {
      // une mesure de type translatable
      // update translationEntry ?
      // console.log('trsLabel', this.trsLabel);
      result = this.trsLabel.wizValue;

      let res: TranslatedEmb = new TranslatedEmb();
      res["keys"] = {};
      this.trsLabel.entryDS.getSource().forEach((iwt: IWizTranslationEntry) => {
        res.keys[iwt.languageCode] = { cod: iwt.languageCode, val: iwt.label};
      });
      result = JSON.stringify(res);
    }


    if (!!result) {
      // la demande
      const updateRequest: UpdateRequest = {
        fei: this.fei, 
        line: this.popupData.line,
        lines: this.popupData.pack, 
        operation: EM_ACTION.UPDATE,
        fkd: this.fkd,
        value: result,
        doSplit: false,
      };

      this.popupData.updates = updateRequest;
      this.popupData.callback(this.popupData); // on appelle la callback avec le pack d'updates

    }

    this.hide();
  }

  /** Sauvegarde, fractionnement de la valeur */
  public saveSplit(): void {
    if (this.ni.wizValue) {
      
      // la demande
      const updateRequest: UpdateRequest = {
        fei: this.fei, 
        line: this.popupData.line,
        lines: this.popupData.pack, 
        operation: EM_ACTION.UPDATE,
        fkd: this.fkd,
        value: this.ni.wizNumber,
        doSplit: true,
      };
      
      this.popupData.updates = updateRequest;
      this.popupData.callback(this.popupData); // on appelle la callback avec le pack d'updates      
      // const meas: MeasureVO = this._pkd.data as MeasureVO;
      // const updates = BaselineEngine.SplitValue(this.fei, this.mainDS.getSource(), this._pkd.fullCode, meas, this.ni.wizNumber);
      // if(!!updates) {
      //   this._popupData.updates = updates;
      //   this._popupData.callback(this._popupData); // on appelle la callback avec le pack d'updates
      // }
    }
    this.hide();
  }

  /** annulation des modifs */
  public doCancel(): void {
    // this.doSaveFlag = false;
    this.hide();
  }


  public showDetail = false; // affichage du grid
  /** affichage du détail des lignes */
  public toggleShowEditDetail(): void {
    this.showDetail = !this.showDetail;
    this.lblShowHide.wizLabel = this.showDetail ? '$.pivot.editpop.hidedetail.$' : '$.pivot.editpop.showdetail.$';
    this.parameters.dialogRef.updateSize(
      PivotCellEditPop.baseWidth + 'px',
      this.showDetail ? PivotCellEditPop.fullHeight + 'px' : PivotCellEditPop.baseHeight + 'px'
    );

    // reposition si nécessaire
    this.parameters.width = PivotCellEditPop.baseWidth + 'px';
    this.parameters.height = this.showDetail ? PivotCellEditPop.fullHeight + 'px' : PivotCellEditPop.baseHeight + 'px';
    const pos: DOMRect = this.elementRef.nativeElement.getBoundingClientRect();
    // console.log('pos', pos);
    const bestXY: [number, number] = PopUtils.setPopCoord(pos, this.parameters, WIZ_PHYSIC_POSITION.BOTTOM_RIGHT);
    // console.log('bestXY', pos);
    const x: string = bestXY[0] + 'px';
    const y: string = bestXY[1] + 'px';
    const targetPosition: any = { left: x, top: y }; // position physique
    this.parameters.dialogRef.updatePosition(targetPosition);
  }

  /* ##############################################################################
                   gestion du colorPicker pour la conf du header
     ############################################################################## */

  /**
   * affichage de la popUp du colorPicker
   * @param pEvent click
   */
  public showColorPicker(pEvent: Event): void {
    pEvent.stopPropagation();
    pEvent.preventDefault();
    ColorPickerPop.show(this.cellBlockBack.nativeElement, pEvent.target, this.textValue, this.backFromPicker.bind(this));
  }

  /**
   * retour de colorPicker, alimentation des couleurs du header
   *
   * @param target le span source pour font ou background
   * @param color la couleur du picker
   */
  public backFromPicker(target: any, color: string): void {
    if(!color) {
      color = '#FFFFFF';
    }
    this.textValue = MathUtils.RGBA2HEX(color);
    this.cellBlockBack.nativeElement.style.backgroundColor = this.textValue;
    
   }

  /** click sur les icones de suppression de style de header */
  public resetColor(who: string): void {
    this.textValue = null;
  }

  public get editLbl(): string {
    if(this.isMultiple) {
      return WizTranslationManager.getInstance().translateKey('$.pivot.mass.edit.$');
    } else {
      return WizTranslationManager.getInstance().translateKey('$.common.save.$');
    }
    
  }

}
